<template>
  <BaseSearchWrapper
    :index-name="indexName"
    filters="content_type:course AND hide_from_calendar:false AND status:live AND (certificate_sponsored_type:public OR certificate_sponsored_type:public OR public-continuous OR open-private)"
    search-profile="wcei-calendar"
    wrapper-classes="w-full col-span-12 flex flex-col gap-y-6"
    @aggregations="handleAggregations"
    @search-client-loaded="searchClient = $event"
  >
    <div
      v-if="isMobileScreen"
      class="md:hidden"
    >
      <MobileCourseFilters :search-client="searchClient" />
    </div>

    <div
      v-else
      class="flex gap-x-4 max-md:hidden"
    >
      <BaseSearchSelect
        attribute="primary_category"
        default-option="All Courses"
        label="Filter by Course Category"
        @filter-changed="handleFilterChanged"
      />
      <BaseSearchSelect
        attribute="course_type"
        default-option="All Formats"
        label="Filter by Course Format"
        @filter-changed="handleFilterChanged"
      />
      <BaseSearchSelect
        :should-transform-items="false"
        attribute="location"
        default-option="All Locations"
        label="Filter by Course Location"
        @filter-changed="handleFilterChanged"
      />

      <AisClearRefinements
        :class-names="{
          'ais-ClearRefinements-button': 'btn btn--outline max-lg:hidden',
        }"
      >
        <template #resetLabel>
          Clear Filters
        </template>
      </AisClearRefinements>
    </div>

    <template v-if="courseGroups.length > 0">
      <template v-for="group in courseGroups">
        <div
          v-if="group.courses.hits.hits.length > 0"
          :key="group.key_as_string"
          class="flex flex-col gap-y-4 lg:gap-y-6"
        >
          <h2
            class="heading-sm md:heading-md border-b-2 border-gray-300 pb-2"
            v-text="group.key_as_string"
          />
          <ul class="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            <CourseSearchResult
              v-for="hit in group.courses.hits.hits"
              :key="hit._id"
              :result="hit._source"
            />
          </ul>
        </div>
      </template>
    </template>
    <div v-else>
      <p v-text="'No courses found for the selected filters.'" />
    </div>
  </BaseSearchWrapper>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { AisClearRefinements } from 'vue-instantsearch/vue3/es'

import BaseSearchSelect from '@js/components/search/BaseSearchSelect.vue'
import BaseSearchWrapper from '@js/components/search/BaseSearchWrapper.vue'
import CourseSearchResult from '@js/components/search/CourseSearchResult.vue'
import MobileCourseFilters from '@js/components/search/MobileCourseFilters.vue'

import { env } from '@js/utils/env'
import { log } from '@js/utils/logger'

import { ElasticsearchCourseBucket } from '@js/types/search/ElasticsearchCourseBucket'

defineOptions({
  name: 'AppCalendarSearch',
})

const searchClient = ref<any | null>(null)
const courseGroups = ref<ElasticsearchCourseBucket[]>([])

// No sort suffix: this component renders only from the aggregation below, whose own
// top_hits sort orders the courses. A suffix would change the routing URL key and break
// existing filter links.
const indexName = computed(() => env.elasticsearch.SEARCH_INDEX)

const isMobileScreen = useMediaQuery('(max-width: 767px)')

const handleFilterChanged = (): void => {
  // Belt and braces on freshness — the aggregation is now read on cache hits too.
  searchClient.value?.clearCache()
}

// Grouping comes from the courses_by_month aggregation the wcei-calendar profile adds
// server-side, returned via the proxy's _rawAggregations passthrough.
const handleAggregations = (aggregations: Record<string, unknown>): void => {
  const monthAggregation = aggregations?.courses_by_month as
    | { buckets?: ElasticsearchCourseBucket[] }
    | undefined

  const buckets = monthAggregation?.buckets

  if (buckets) {
    courseGroups.value = buckets
    return
  }

  courseGroups.value = []

  log({
    file: 'src/wcei/js/components/search/AppCalendarSearch.vue',
    message: 'Expected aggregation data not found in search response',
  })
}

</script>
